export type EditorSelector = {
  blockIds?: string[];
  elementIds?: string[];
  commentId?: string;
  refreshKey?: string;
  presenceUserId?: string;
  activityRunId?: string;
  activityLabel?: string;
};

// ===== Collaborative Presentation Constants =====

/**
 * Grace period for presence-related timeouts (30 seconds)
 * Used for:
 * - Follower disconnect grace period
 * - Moderation button glow after presenter stops
 */
export const PRESENCE_GRACE_PERIOD_MS = 30000;

// ===== Collaborative Presentation Types (CP-010) =====

/**
 * Presentation mode determines how viewport is synchronized
 * - 'free': Continuous viewport sync (pan/zoom)
 * - 'frames': Frame-based navigation sync
 */
export type PresentationMode = 'free' | 'frames';

/**
 * Settings for presentation display
 */
export interface PresentationSettings {
  fillScreen: boolean;
  blackBackground: boolean;
  hideToolbar: boolean;
}

/**
 * Presentation state broadcasted via Awareness
 */
export interface PresentationState {
  isPresenting: boolean;
  mode: PresentationMode;
  settings?: PresentationSettings;
  // Frame mode: Current frame index and ID
  frameIndex?: number;
  frameId?: string;
  // Heartbeat timestamp for freshness check (prevents stale moderator detection)
  heartbeatTs?: number;
}

/**
 * Viewport state for synchronization
 */
export interface ViewportState {
  zoom: number;
  centerX: number;
  centerY: number;
  visibleBounds?: {
    x: number;
    y: number;
    w: number;
    h: number;
  };
  frameIndex?: number;
  frameId?: string;
}

/**
 * Interface for presentation broadcasting (Dependency Inversion)
 * Allows different implementations (Awareness, WebSocket, etc.)
 */
export interface IPresentationBroadcaster {
  /**
   * Start broadcasting presentation state
   */
  startPresentation(mode: PresentationMode): void;

  /**
   * Stop broadcasting and clear state
   */
  stopPresentation(): void;

  /**
   * Update presentation mode
   */
  setMode(mode: PresentationMode): void;

  /**
   * Broadcast viewport update
   */
  broadcastViewport(viewport: ViewportState): void;

  /**
   * Get current presentation state (if any)
   */
  getCurrentState(): PresentationState | null;
}

// ===== Follower Types (CP-007) =====

/**
 * Presenter info as seen by a follower
 */
export interface PresenterInfo {
  clientId: number;
  userName?: string;
  presentation: PresentationState;
  viewport?: ViewportState;
}

// ===== State Machine Types (SOLID Architecture) =====

/**
 * Discriminated Union for Presentation State Machine
 *
 * SOLID Principles:
 * - Single Source of Truth: All presentation state in one place
 * - Type Safety: Invalid states are impossible at compile time
 * - Explicit Transitions: Only defined transitions are allowed
 */
export type PresentationMachineState =
  | { type: 'idle' }
  | {
      type: 'moderating_free';
      settings: PresentationSettings;
    }
  | {
      type: 'moderating_frames';
      frameIndex: number;
      frameId: string;
      totalFrames: number;
      settings: PresentationSettings;
    }
  | {
      type: 'following_free';
      presenterId: string;
      presenterName?: string;
    }
  | {
      type: 'following_frames';
      presenterId: string;
      presenterName?: string;
      frameIndex: number;
      frameId: string;
      isJoined: boolean; // true = in fullscreen presentation view
    };

/**
 * Helper type guards for state machine
 */
export const StateMachineHelpers = {
  isModerating: (
    s: PresentationMachineState
  ): s is
    | Extract<PresentationMachineState, { type: 'moderating_free' }>
    | Extract<PresentationMachineState, { type: 'moderating_frames' }> =>
    s.type === 'moderating_free' || s.type === 'moderating_frames',

  isFollowing: (
    s: PresentationMachineState
  ): s is
    | Extract<PresentationMachineState, { type: 'following_free' }>
    | Extract<PresentationMachineState, { type: 'following_frames' }> =>
    s.type === 'following_free' || s.type === 'following_frames',

  isInFrameMode: (s: PresentationMachineState): boolean =>
    s.type === 'moderating_frames' || s.type === 'following_frames',

  getFrameIndex: (s: PresentationMachineState): number | null =>
    'frameIndex' in s ? s.frameIndex : null,

  getPresenterId: (s: PresentationMachineState): string | null =>
    'presenterId' in s ? s.presenterId : null,

  getSettings: (s: PresentationMachineState): PresentationSettings | null =>
    'settings' in s ? s.settings : null,
};

/**
 * Transition result for state machine operations
 */
export interface TransitionResult {
  success: boolean;
  previousState: PresentationMachineState;
  newState: PresentationMachineState;
  error?: string;
}

/**
 * Interface for viewport synchronization (Dependency Inversion)
 */
export interface IViewportController {
  /**
   * Set viewport directly (for Lerp interpolation)
   */
  setViewport(zoom: number, centerX: number, centerY: number): void;

  /**
   * Fit the visible world bounds from the presenter. Keeps free-mode following
   * visually equivalent across different follower viewport sizes.
   */
  getViewportForBounds?(bounds: {
    x: number;
    y: number;
    w: number;
    h: number;
  }): ViewportState;

  /**
   * Smooth transition to target viewport
   */
  transitionTo(
    viewport: ViewportState,
    options: { duration: number; easing?: string }
  ): void;

  /**
   * Navigate to specific frame (for frame mode)
   */
  navigateToFrame(frameIndex: number): Promise<void>;

  /**
   * Get current viewport state
   */
  getCurrentViewport(): ViewportState;
}
